<script setup>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Tag from "primevue/tag";
import { ref, watch, computed, onMounted } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { usePage } from "@inertiajs/inertia-vue3";
import { debounce } from "../../../utils/debounce";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import CompanyEditForm from "../../../Components/Management/CompanyEditForm.vue";

const props = defineProps({
    companies:      { type: Object,  default: () => ({ data: [], links: [], from: 0, to: 0, total: 0 }) },
    filters:        { type: Object,  default: () => ({ search: '' }) },
    flash:          { type: Object,  default: null },
    editingCompany: { type: Object,  default: null }, // super_admin: inline edit target
    adminCompany:   { type: Object,  default: null }, // admin: their own company
});

const page     = usePage();
const confirm  = useConfirm();
const toast    = useToast();
const search   = ref(props.filters?.search ?? '');

const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');
const isAdmin      = computed(() => page.props.value?.auth?.user?.role === 'admin');

// ── Mode state ─────────────────────────────────────────────────────────────
// 'list' = show DataTable   |   'edit' = show CompanyEditForm inline
const mode = ref('list');

// The company currently shown in the edit form
const activeEditCompany = computed(() =>
    props.editingCompany ?? props.adminCompany ?? null
);

// Enter correct initial mode
onMounted(() => {
    if (props.adminCompany || props.editingCompany) {
        mode.value = 'edit';
    }
});

// React to Inertia prop updates (e.g. when ?edit=uuid is added/removed)
watch(
    () => [props.adminCompany, props.editingCompany],
    ([adminCo, editCo]) => {
        mode.value = (adminCo || editCo) ? 'edit' : 'list';
    }
);

// ── Tab label override ──────────────────────────────────────────────────────
const tabOverrides = computed(() =>
    isAdmin.value ? ['Unternehmensdetails'] : []
);

// ── List actions ────────────────────────────────────────────────────────────
const updateSearch = debounce(() => {
    Inertia.get('/management/companies', { search: search.value }, { preserveState: true, replace: true });
}, 500);

watch(search, () => updateSearch());

const openEdit = (company) => {
    Inertia.get(
        '/management/companies',
        { company: company.uuid },
        { preserveState: true, preserveScroll: true }
    );
};

const backToList = () => {
    Inertia.get('/management/companies', {}, { preserveState: false });
};

const getStatusSeverity = (isActive) => isActive ? 'success' : 'danger';
const getStatusLabel    = (isActive) => isActive ? 'Aktiv' : 'Inaktiv';

const confirmDelete = (company) => {
    confirm.require({
        message: `Sind Sie sicher, dass Sie das Unternehmen "${company.name}" löschen möchten? Alle zugehörigen Daten könnten betroffen sein.`,
        header: 'Löschen bestätigen',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Löschen',
        rejectLabel: 'Abbrechen',
        accept: () => {
            Inertia.delete(`/management/companies/${company.uuid}`, {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Erfolgreich',
                        detail: 'Unternehmen wurde gelöscht.',
                        life: 5000,
                    });
                }
            });
        }
    });
};
</script>

<template>
    <Layout :flash="flash">
        <Head title="Unternehmen" />

        <div class="space-y-6">
            <ManagementHeader
                title="Unternehmen Management"
                module="companies"
                :activeIndex="0"
                :tabOverrides="tabOverrides"
            >
                <template #actions>
                    <!-- + Hinzufügen: only for super_admin in list mode -->
                    <Link v-if="isSuperAdmin && mode === 'list'"
                          href="/management/companies/create">
                        <Button label="Hinzufügen" icon="pi pi-plus" severity="success" class="!px-4 !py-2" />
                    </Link>
                </template>
            </ManagementHeader>

            <!-- ── LIST MODE (super_admin only) ─────────────────────────── -->
            <div v-if="mode === 'list'"
                 class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

                <!-- Search bar -->
                <div class="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                    <IconField iconPosition="left" class="w-full max-w-md">
                        <InputIcon class="pi pi-search"></InputIcon>
                        <InputText v-model="search"
                                   class="w-full"
                                   placeholder="Suche nach Name oder Email..." />
                    </IconField>
                </div>

                <DataTable :value="companies.data"
                           :rows="20"
                           dataKey="id"
                           responsiveLayout="scroll"
                           class="p-datatable-sm">
                    <Column field="name"       header="Name"       sortable class="font-semibold"></Column>
                    <Column field="email"      header="Email"      sortable></Column>
                    <Column field="city"       header="Stadt"      sortable></Column>
                    <Column field="is_active"  header="Status"     sortable>
                        <template #body="slotProps">
                            <Tag :value="getStatusLabel(slotProps.data.is_active)"
                                 :severity="getStatusSeverity(slotProps.data.is_active)" />
                        </template>
                    </Column>
                    <Column field="created_at" header="Erstellt am" sortable>
                        <template #body="slotProps">
                            <span class="text-gray-500 text-xs">{{ slotProps.data.created_at }}</span>
                        </template>
                    </Column>
                    <Column header="Aktionen"
                            headerStyle="width: 8rem; text-align: center"
                            bodyStyle="text-align: center">
                        <template #body="slotProps">
                            <div class="flex justify-center gap-3">
                                <!-- Edit: opens inline form instead of navigating away -->
                                <Button icon="pi pi-pencil"
                                        severity="info"
                                        outlined
                                        class="!p-2"
                                        @click.stop="openEdit(slotProps.data)" />
                                <!-- Delete: super_admin only -->
                                <Button icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        class="!p-2"
                                        @click.stop="confirmDelete(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="py-12 text-center">
                            <i class="pi pi-building text-4xl text-gray-200 mb-4" />
                            <p class="text-gray-400">Keine Unternehmen gefunden.</p>
                        </div>
                    </template>
                </DataTable>

                <!-- Pagination -->
                <div class="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                    <div>
                        Zeige {{ companies.from }} bis {{ companies.to }} von {{ companies.total }} Einträgen
                    </div>
                    <div class="flex gap-2">
                        <Link v-for="link in companies.links"
                              :key="link.label"
                              :href="link.url || '#'"
                              v-html="link.label"
                              :class="[
                                'px-3 py-1 rounded border transition-colors',
                                link.active ? 'bg-black text-[#FFF053] border-black' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                                !link.url ? 'opacity-50 cursor-not-allowed' : ''
                              ]" />
                    </div>
                </div>
            </div>

            <!-- ── EDIT MODE (admin: always | super_admin: after clicking edit) ── -->
            <div v-else-if="mode === 'edit' && activeEditCompany"
                 class="bg-white rounded-xl shadow-sm border border-gray-100 p-8">

                <!-- Header row: back button + company name — super_admin only -->
                <div v-if="isSuperAdmin" class="flex items-center gap-4 mb-6">
                    <button type="button"
                            class="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            @click="backToList">
                        <i class="pi pi-arrow-left text-gray-500" />
                    </button>
                    <h2 class="text-2xl font-bold text-gray-800">
                        Unternehmen bearbeiten:
                        <span class="text-gray-500 font-normal">{{ activeEditCompany.name }}</span>
                    </h2>
                </div>

                <CompanyEditForm
                    :company="activeEditCompany"
                    :showBackButton="isSuperAdmin"
                    @saved="isSuperAdmin ? backToList() : null"
                    @cancel="backToList"
                />
            </div>
        </div>
    </Layout>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
    background: transparent;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1rem;
}

:deep(.p-datatable-tbody > tr > td) {
    padding: 1rem;
    border-bottom: 1px solid #f8fafc;
}

:deep(.p-datatable-tbody > tr:hover) {
    background-color: #fcfcfd;
}
</style>
